import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { DENOMINAZIONE, LOGHI } from '../../lib/marchio';
import type { Intestazione } from '../../lib/tipi';

/**
 * Loghi e denominazione del poligono.
 *
 * I loghi sono risorse statiche dell'applicazione: si vedono subito, anche prima che esista
 * una sessione e anche su un database appena creato. La denominazione arriva invece da
 * `configurazione`, che l'amministratore può cambiare da Impostazioni (9b); finché la
 * risposta non arriva si usa il valore di `marchio.ts`, così non lampeggia mai un nome
 * diverso da quello vero.
 */

/*
 * La richiesta si fa una volta sola per caricamento di pagina.
 *
 * `useIntestazione` è chiamata da quattordici punti — i tre gusci, l'accesso, la sessione
 * scaduta, l'export, la scheda da stampare, il dettaglio del tiratore — e ciascuno faceva
 * la propria `fetch`. Misurando l'avvio dell'amministrazione si vedevano due
 * `/pubblico/intestazione` partire nello stesso millisecondo, dal guscio e dalla schermata
 * dentro il guscio; aprendo la scheda di un socio ne partiva una terza.
 *
 * Il dato è la denominazione del poligono e i suoi loghi: uno solo per tutta
 * l'applicazione, che non cambia mentre la si guarda. Si tiene quindi la **promessa**, non
 * il risultato: chi arriva mentre la prima richiesta è ancora in volo si aggancia a quella
 * invece di aprirne un'altra, che è il caso vero — i due chiamanti dell'avvio si montano a
 * un millisecondo di distanza.
 *
 * Non è una cache fra sessioni e non prova a esserlo: vive quanto la pagina. Chi cambia la
 * denominazione da Impostazioni la rivede al prossimo caricamento, che è quando la rivedeva
 * anche prima — nessuna delle quattordici chiamate si riaggiornava da sola.
 */
let inVolo: Promise<Intestazione | null> | null = null;

function chiediIntestazione(): Promise<Intestazione | null> {
  inVolo ??= api
    .get<Intestazione>('/pubblico/intestazione')
    .catch(() => {
      /*
       * Fallita, si riparte da capo la prossima volta.
       *
       * Tenere una promessa respinta significherebbe che una sola disconnessione al primo
       * millisecondo condanna tutta la visita al nome di ripiego, anche dopo che la rete è
       * tornata. Chi si monta più tardi riprova.
       */
      inVolo = null;
      return null;
    });
  return inVolo;
}

export function useIntestazione(): Intestazione {
  const [dati, setDati] = useState<Intestazione>({ denominazione: DENOMINAZIONE });

  useEffect(() => {
    chiediIntestazione()
      .then((risposta) => {
        /*
         * Si accetta solo una risposta della forma attesa.
         *
         * `lib/api.ts` non distingue fra corpo vuoto e corpo illeggibile: entrambi tornano
         * `null`, dichiarato come se fosse il dato. Scritto senza guardia, un 200 malformato
         * sostituirebbe il ripiego con niente, e la riga sotto — `intestazione.denominazione`
         * — cadrebbe nel confine d'errore. Su queste schermate significa che la sola porta
         * dell'applicazione non si apre più, e che non c'è modo di entrare per rimediare.
         */
        if (risposta && typeof risposta.denominazione === 'string') setDati(risposta);
      });
  }, []);

  return dati;
}

/**
 * I due marchi affiancati, per le schermate d'accesso.
 *
 * Sulle testate ne compare uno solo, e per una ragione di misura: sono alte fra i 34 e i
 * 44 px, dove due marchi diventerebbero due macchie indistinguibili. Qui la ragione non si
 * applica — c'è lo spazio, e l'associazione è nata dall'unione delle due realtà, quindi
 * mostrarne una sola sulla schermata che accoglie tutti è dire metà del nome.
 *
 * L'ordine è quello di `LOGHI`, lo stesso stampato in testa ai documenti (§ 8): i due posti
 * dove i marchi stanno insieme si leggono nello stesso verso.
 *
 * `alt` vuoto perché la denominazione è sempre lì accanto, scritta: sono decorativi nel
 * senso preciso della parola, e un lettore di schermo che li annunciasse ripeterebbe due
 * volte un nome che sta già nel testo. È anche la convenzione già seguita da tutte le altre
 * testate dell'applicazione.
 */
export function Loghi() {
  return (
    <div className="accesso__loghi">
      {LOGHI.map((percorso) => (
        <div className="accesso__logo" key={percorso}>
          <img src={percorso} alt="" />
        </div>
      ))}
    </div>
  );
}

export function Marchio({ intestazione }: { intestazione: Intestazione }) {
  return (
    <div className="accesso__marchio">
      <Loghi />
      <div className="accesso__denominazione">
        <div className="accesso__nome">{intestazione.denominazione}</div>
      </div>
    </div>
  );
}
